import { AnyAction, combineReducers } from 'redux';
import { success, error } from '@redux-requests/core';

import { Workflow } from 'shared/types/workflow';
import { IndexableObjectType } from 'shared/types/common';

import * as applicationUserActions from '../applicationUser/actions';

import * as actions from './actions';
import { EmptyWorkflow } from './constants';
import { WorkflowListState } from './types';

const workflowListReducer = (state: WorkflowListState = [], action: AnyAction) => {
  switch (action.type) {
    case actions.HANDLE_WORKFLOWS_FETCH:
      return action.payload.workflows;
    case success(actions.CREATE_WORKFLOW):
      const restOfWorkflows = state.map((workflow) => ({ ...workflow, isNew: false }));

      // We assign an `isNew` property to the new workflow to identify it from the rest.
      return restOfWorkflows.concat({ ...action.payload.data, isNew: true });
    case success(actions.DELETE_WORKFLOW):
      return state.filter((workflow) => workflow.guid !== action.payload.workflow.guid);
    case success(actions.EDIT_WORKFLOW):
      const { data: updatedWorkflow } = action.payload;

      return state.map((workflow) => workflow.guid === updatedWorkflow.guid
        ? updatedWorkflow
        : workflow
      );
    case actions.ADD_API_CREDENTIALS_TO_WORKFLOW:
      const { workflowGuid, apiCredentials } = action.payload;

      return state.map((workflow) => {
        if (workflow.guid === workflowGuid) {
          return {
            ...workflow,
            destination: {
              ...workflow.destination,
              apiCredentials,
            },
          };
        }

        return workflow;
      });
    default:
      return state;
  }
};

const isCreatingWorkflowReducer = (state = false, action: AnyAction) => {
  switch (action.type) {
    case actions.CREATE_WORKFLOW:
      return true;
    case error(actions.CREATE_WORKFLOW):
    case success(applicationUserActions.GET_API_CREDENTIALS):
    case error(applicationUserActions.GET_API_CREDENTIALS):
      return false;
    default:
      return state;
  }
};

const currentWorkflowReducer = (state: Workflow = EmptyWorkflow, action: AnyAction) => {
  switch (action.type) {
    case actions.GET_WORKFLOW:
    case error(actions.GET_WORKFLOW):
      return null;
    case success(actions.GET_WORKFLOW):
      return action.payload.data;
    default:
      return state;
  }
};

const fetchingCredentialsMapReducer = (state: IndexableObjectType = {}, action: AnyAction) => {
  switch (action.type) {
    case actions.GET_WORKFLOW_API_CREDENTIALS: {
      const { workflowGuid } = action.payload;

      return {
        ...state,
        [workflowGuid]: true,
      };
    }
    case actions.ADD_API_CREDENTIALS_TO_WORKFLOW:
    case error(actions.GET_WORKFLOW_API_CREDENTIALS):
      const { workflowGuid } = action.payload;

      return {
        ...state,
        [workflowGuid]: false,
      };
    default:
      return state;
  }
};

const isDeletingWorkflowReducer = (state = false, action: AnyAction) => {
  switch (action.type) {
    case actions.DELETE_WORKFLOW:
      return true;
    case error(actions.DELETE_WORKFLOW):
    case success(actions.DELETE_WORKFLOW):
      return false;
    default:
      return state;
  }
};

export default combineReducers({
  list: workflowListReducer,
  fetchingCredentialsMap: fetchingCredentialsMapReducer,
  isCreatingWorkflow: isCreatingWorkflowReducer,
  isDeletingWorkflow: isDeletingWorkflowReducer,
  currentWorkflow: currentWorkflowReducer,
});
